import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { setAccountType } from 'actions';
import { rem, Button } from 'design-system';
import { SettingsItem, SettingsItemRightContent, } from 'design-system/SettingsItem';
import Modal from 'utils/packages/Modal';
import { DUMMY_ACCOUNT_ICON_MAP } from 'features/accounts/hooks/useDummyAccountLogo';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { muteAll, useFetchOld } from 'hooks';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { selectShowNewPhysicalAssets } from 'features/accounts/selectors';
import { selectIsTrackYourMortgageEnabled } from 'features/credit-score/selectors';
import { PHYSICAL_ASSET_TYPES } from 'features/accounts/constants';
import i18n from '../../../../i18n';
import { isWeb } from '../../../../constants';
const accountTypeMenuOptions = [
    {
        type: 'CHECKING',
        title: `${i18n.t('current')} account`,
    },
    {
        type: 'SAVINGS',
        title: 'Savings account',
    },
    {
        type: 'INVESTMENT',
        title: 'Investment account',
    },
    {
        type: 'PENSION',
        title: 'Pension',
    },
];
const accountTypeMenuOptionsDebt = [
    {
        type: 'CREDITCARD',
        title: 'Credit card',
    },
    {
        type: 'LOAN',
        title: 'Loan',
    },
    {
        type: 'MORTGAGE',
        title: 'Mortgage',
    },
];
const accountTypeMenuOptionsAsset = [
    {
        type: 'REAL_ESTATE',
        title: 'Real estate',
    },
    {
        type: 'VEHICLE',
        title: 'Vehicle',
    },
    {
        type: 'OTHER',
        title: 'Other assets',
    },
];
const AccountTypeMenu = ({ accountId, currentAccountType, isManual, }) => {
    const [, , , fetch] = useFetchOld(muteAll);
    const isDebt = currentAccountType === 'LOAN' ||
        currentAccountType === 'MORTGAGE' ||
        currentAccountType === 'CREDITCARD';
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const isTrackYourMortgageEnabled = useAppSelector(selectIsTrackYourMortgageEnabled);
    const isPhysicalAsset = useMemo(() => PHYSICAL_ASSET_TYPES.has(currentAccountType), [currentAccountType]);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'offlineAccounts',
    });
    const refreshAccounts = useRefreshAccounts();
    const handleBySetAccountType = useCallback((type) => () => {
        Modal.hide();
        if (type === currentAccountType)
            return;
        fetch(setAccountType(type, accountId)).then((response) => {
            if (response) {
                refreshAccounts(type, currentAccountType);
            }
        });
    }, [currentAccountType, accountId, fetch, refreshAccounts]);
    const handleByUpgrade = useCallback(() => {
        Modal.hide();
        onPressUpgrade();
    }, [onPressUpgrade]);
    const upgradebutton = useMemo(() => (<SettingsItemRightContent>
        <Button brandLight square medium cards title="Upgrade" onPress={handleByUpgrade}/>
      </SettingsItemRightContent>), [handleByUpgrade]);
    const options = useMemo(() => {
        if (isDebt) {
            return isManual && isTrackYourMortgageEnabled
                ? accountTypeMenuOptionsDebt
                : accountTypeMenuOptionsDebt.filter(({ type }) => type !== 'MORTGAGE');
        }
        if (isPhysicalAsset && showNewPhysicalAssets) {
            return accountTypeMenuOptionsAsset;
        }
        return [
            ...accountTypeMenuOptions,
            ...(!showNewPhysicalAssets ? accountTypeMenuOptionsAsset : []),
        ];
    }, [
        isDebt,
        isManual,
        isPhysicalAsset,
        isTrackYourMortgageEnabled,
        showNewPhysicalAssets,
    ]);
    const styles = useStyles(styleSet);
    return (<View style={styles.container}>
      {options.map(({ type, title }) => {
            const disabled = (type === 'PENSION' ||
                type === 'REAL_ESTATE' ||
                type === 'VEHICLE' ||
                type === 'OTHER') &&
                !isUnlockedForSpace;
            const itemProps = {
                title,
                selected: type === currentAccountType,
                icon: disabled ? (<View style={styles.opaque}>{DUMMY_ACCOUNT_ICON_MAP[type]}</View>) : (DUMMY_ACCOUNT_ICON_MAP[type]),
                children: disabled ? upgradebutton : undefined,
            };
            if (isWeb) {
                return (<TouchableBounceCard key={type} onDark noMargin style={styles.card} onPress={disabled ? undefined : handleBySetAccountType(type)}>
              <SettingsItem light disabled style={styles.item} titleProps={disabled ? { style: styles.opaque } : undefined} {...itemProps}/>
            </TouchableBounceCard>);
            }
            return (<SettingsItem key={type} onPress={handleBySetAccountType(type)} disabled={disabled} titleProps={disabled ? { style: styles.opaque } : undefined} {...itemProps}/>);
        })}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        ...(!isWeb && {
            marginTop: rem(20),
        }),
    },
    card: {
        marginTop: rem(16),
        backgroundColor: colors.background.light,
    },
    item: {
        paddingHorizontal: rem(8),
    },
    opaque: {
        opacity: 0.5,
    },
}));
export default AccountTypeMenu;
